import type { AuthProvider } from '@refinedev/core'
import type { QueryClient } from '@tanstack/react-query'
import type { AdminClient } from '~/api/adminClient'
import { meInput } from '~/tenancy/hostSlug'

export const ME_QUERY_KEY = ['me'] as const

/**
 * `auth.me` through the query cache: access control asks on every `can()`,
 * so the answer is shared and kept for a minute. It names the host tenant, so
 * the session carries the panel language that host resolves to.
 */
export function createMeLoader(client: AdminClient, queryClient: QueryClient) {
  return () =>
    queryClient.fetchQuery({
      queryKey: ME_QUERY_KEY,
      queryFn: () => client.auth.me(meInput()),
      staleTime: 60_000,
    })
}

/**
 * `auth.me` asked again now, whatever the cache holds, and kept for the
 * loader: a membership or role changed by someone else shows at once.
 */
export function refreshMe(client: AdminClient, queryClient: QueryClient) {
  return queryClient.fetchQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => client.auth.me(meInput()),
    staleTime: 0,
  })
}

/**
 * Drops the whole query cache after a successful login and on logout: `me`,
 * the tenant gate, Refine's access-control answers and every data list depend
 * on who is signed in, so a second user on the same page load never sees the
 * first user's role, tenants or records.
 */
export function withSessionQueryReset(
  authProvider: AuthProvider,
  queryClient: QueryClient,
): AuthProvider {
  return {
    ...authProvider,
    login: async (params: unknown) => {
      const result = await authProvider.login(params)
      if (result.success) {
        queryClient.clear()
      }
      return result
    },
    logout: async (params: unknown) => {
      queryClient.clear()
      return authProvider.logout(params)
    },
  }
}
